import { Button, InputGroup, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import React from 'react'
import Client from '../../../client'
import EmptyPlaceholder from '../../../components/EmptyPlaceholder'
import DateRangeFilter from '../../../components/filters/DateRangeFilter'
import { LoadableSelect } from '../../../components/Inputs'
import InteractiveDatetime from '../../../components/InteractiveDatetime'
import { camelToTitle, camelize, formatMoneyWithCurrency } from '../../../utils'
import ProductTag from '../../product_manager/components/ProductTag'
import TagSelector from '../../product_manager/components/TagSelector'
import { ColumnParams } from './types'
import './style.scss'
import ListingErrorWithOverflowTooltip from './ListingErrorWithOverflowTooltip'
import { UprightStyle } from '../../../types'

const styles: UprightStyle = {
  columnStyle: { margin: 0, textAlign: 'left' }
}

export const SearchableFilter = ({ meta, onMetaChange, metaKey }): React.ReactElement => (
  <InputGroup
    rightElement={
      <Button
        minimal
        intent={Intent.PRIMARY}
        icon={IconNames.SEARCH}
        onClick={() => onMetaChange(metaKey, meta[metaKey])}
        disabled={!meta[metaKey]}
      />
    }
    value={meta[metaKey] || ''}
    onChange={e => {
      if (!e.target.value) {
        onMetaChange(metaKey, null)
      } else {
        onMetaChange(metaKey, e.target.value, false)
      }
    }}
    onKeyUp={e => {
      if (e.key === 'Enter') {
        onMetaChange(metaKey, meta[metaKey])
      }
    }}
  />
)

export const listingActions = (channel, openDrawer): ColumnParams => ({
  Header: '',
  Cell: row => (
    <Button
      onClick={() => openDrawer(row.original.id)}
      icon={IconNames.EYE_OPEN}
      small
      minimal
      intent={Intent.PRIMARY}
    />
  ),
  sortable: false,
  filterable: false,
  width: 40
})

export const inventoryLocation = (meta, onMetaChange): ColumnParams => ({
  id: 'inventory_location',
  Header: 'Inventory Location',
  accessor: d => <EmptyPlaceholder>{d.inventoryLocationName}</EmptyPlaceholder>,
  sortable: true,
  filterable: true,
  headerClassName: 'allow-overflow nuke-top-margin-loadable-select',
  minWidth: 135,
  Filter: () => (
    <LoadableSelect
      load={Client.inventoryLocationIndex}
      process={d => d.inventoryLocations.map(u => ({ value: u.id, label: u.name }))}
      onChange={v => onMetaChange('inventoryLocationId', v)}
      value={meta.inventoryLocationId}
      style={{ margin: 0, textAlign: 'left' }}
      className="users-filter"
      autoload={meta.inventoryLocationId}
      stableOptionOrder // sorting for this field done on API
      virtualized
    />
  )
})

export const carrier = (meta, onMetaChange): ColumnParams => ({
  id: 'carrier',
  Header: 'Carrier',
  accessor: d => <EmptyPlaceholder>{d.carrierName}</EmptyPlaceholder>,
  sortable: false,
  filterable: true,
  headerClassName: 'allow-overflow nuke-top-margin-loadable-select',
  minWidth: 135,
  Filter: () => (
    <LoadableSelect
      load={Client.carriersIndex}
      process={d => d.carriers.map(u => ({ value: u.id, label: u.name }))}
      onChange={v => onMetaChange('carrierId', v)}
      value={meta.carrierId}
      style={{ margin: 0, textAlign: 'left' }}
      className="users-filter"
      autoload={meta.carrierId}
      virtualized
    />
  )
})

export const supplier = (meta, onMetaChange): ColumnParams => ({
  id: 'supplier',
  Header: 'Supplier',
  accessor: d => <EmptyPlaceholder>{d.supplierName}</EmptyPlaceholder>,
  sortable: false,
  filterable: true,
  headerClassName: 'allow-overflow nuke-top-margin-loadable-select',
  minWidth: 135,
  Filter: () => (
    <LoadableSelect
      load={Client.suppliersIndex}
      process={d => d.suppliers.map(u => ({ value: u.id, label: u.name }))}
      onChange={v => onMetaChange('supplierId', v)}
      value={meta.supplierId}
      style={styles.columnStyle}
      className="users-filter"
      autoload={meta.supplierId}
      virtualized
    />
  )
})

export const identityColumn = (fieldName, getFormat = null): ColumnParams => ({
  id: fieldName,
  Header: camelToTitle(fieldName),
  accessor: d => (getFormat ? getFormat(d[fieldName]) : d[fieldName]),
  sortable: false
})

export const currencyAwareIdentityColumn = (fieldName, getFormat): ColumnParams => ({
  id: fieldName,
  Header: camelToTitle(fieldName),
  accessor: d => (getFormat ? getFormat(d[fieldName], d.currencyCode) : d[fieldName]),
  sortable: false
})

export const identityColumnWithIdSuffix = (fieldPrefix, getFormat = null): ColumnParams => ({
  id: `${fieldPrefix}Id`,
  Header: `${camelToTitle(fieldPrefix)} ID`,
  accessor: d => (getFormat ? getFormat(d[`${fieldPrefix}Id`]) : d[`${fieldPrefix}Id`]),
  sortable: false
})

export const formatMoneyHidingZero = (value, currency = 'USD'): ColumnParams =>
  parseFloat(value) !== 0 ? formatMoneyWithCurrency(value, currency) : null

export const title = (meta, onMetaChange): ColumnParams => ({
  Header: 'Title',
  id: 'title',
  sortable: false,
  filterable: true,
  minWidth: 185,
  accessor: d => d.title,
  Filter: () => <SearchableFilter meta={meta} onMetaChange={onMetaChange} metaKey="title" />
})

export const sku = (meta, onMetaChange): ColumnParams => ({
  Header: 'SKU',
  id: 'sku',
  accessor: d => <EmptyPlaceholder>{d.sku}</EmptyPlaceholder>,
  sortable: false,
  filterable: true,
  Filter: () => <SearchableFilter meta={meta} onMetaChange={onMetaChange} metaKey="sku" />
})

export const createdAt = (meta, onMetaChange): ColumnParams => ({
  id: 'created_at',
  Header: 'Created At',
  accessor: d => <InteractiveDatetime value={d.createdAt} />,
  sortable: true,
  filterable: true,
  Filter: () => <DateRangeFilter meta={meta} onMetaChange={onMetaChange} metaKey="createdAt" />
})

export const startTime = (meta, onMetaChange): ColumnParams => ({
  id: 'start_time',
  Header: 'Start Time',
  accessor: d => <EmptyPlaceholder>{d.startTime && <InteractiveDatetime value={d.startTime} />}</EmptyPlaceholder>,
  sortable: true,
  filterable: true,
  Filter: () => (
    <DateRangeFilter
      meta={meta}
      onMetaChange={onMetaChange}
      metaKey="startTime"
      dateRangeProps={{ maxDate: undefined }}
    />
  )
})

export const user = (meta, onMetaChange): ColumnParams => ({
  Header: 'User',
  id: 'username',
  headerClassName: 'allow-overflow nuke-top-margin-loadable-select',
  sortable: true,
  accessor: d => <EmptyPlaceholder>{d.user && d.user.username}</EmptyPlaceholder>,
  filterable: true,
  width: 300,
  Filter: () => (
    <LoadableSelect
      load={Client.users}
      process={d => d.users.map(u => ({ value: u.id, label: u.username }))}
      value={meta.userId}
      className="users-filter"
      style={styles.columnStyle}
      autoload={meta.userId}
      onChange={v => onMetaChange('userId', v)}
      multi
      removeSelected
    />
  )
})

export const endTime = (meta, onMetaChange): ColumnParams => ({
  id: 'end_time',
  Header: 'End Time',
  accessor: d => <EmptyPlaceholder>{d.endTime && <InteractiveDatetime value={d.endTime} />}</EmptyPlaceholder>,
  sortable: true,
  filterable: true,
  Filter: () => (
    <DateRangeFilter
      meta={meta}
      onMetaChange={onMetaChange}
      metaKey="endTime"
      dateRangeProps={{ maxDate: undefined }}
    />
  )
})

export const categoryPath = (meta, onMetaChange): ColumnParams => ({
  id: 'categoryName',
  Header: 'Category',
  accessor: d => <EmptyPlaceholder>{d.categoryPath}</EmptyPlaceholder>,
  sortable: false,
  filterable: true,
  Filter: () => <SearchableFilter meta={meta} onMetaChange={onMetaChange} metaKey="categoryName" />
})

export const productId = (meta, onMetaChange): ColumnParams => ({
  id: 'productId',
  Header: 'Upright Product ID',
  Cell: row => (
    <>
      {row.original.productId}
      {/* {<div>Controlled</div>} */}
    </>
  ),
  className: 'controlled-tag',
  sortable: false,
  filterable: true,
  Filter: () => <SearchableFilter meta={meta} onMetaChange={onMetaChange} metaKey="productId" />,
  width: 135
})

export const tags = (meta, onMetaChange, limitResults?): ColumnParams => ({
  id: 'tags',
  headerClassName: 'allow-overflow nuke-top-margin-loadable-select normalize-heights-tag-selector',
  Header: 'Tags',
  Cell: row => row.original.productTags.map(t => <ProductTag key={t} title={t} />),
  sortable: false,
  filterable: true,
  width: 235,
  Filter: () => (
    <TagSelector
      onChange={v => onMetaChange('tags', v)}
      value={meta.tags}
      createable={false}
      limitResults={limitResults}
    />
  )
})

export const errors = (
  meta: any,
  onMetaChange: Function,
  columnId: string,
  APIMethod: Function
): [ColumnParams] | [] => {
  if (meta && meta.tab && meta.tab !== 'attention-required') return []

  const snakeCaseToCamelCase = camelize(columnId)

  return [
    {
      id: columnId,
      Header: 'Errors',
      accessor: (d: any): JSX.Element => (
        <EmptyPlaceholder>
          {d[snakeCaseToCamelCase] && <ListingErrorWithOverflowTooltip error={d[snakeCaseToCamelCase]} />}
        </EmptyPlaceholder>
      ),
      sortable: true,
      width: 400,
      filterable: true,
      headerClassName: 'allow-overflow',
      Filter: () => (
        <LoadableSelect
          load={APIMethod}
          process={d => d[snakeCaseToCamelCase]}
          value={meta.listingErrors}
          className="users-filter errors-column"
          style={styles.columnStyle}
          autoload={meta.listingErrors}
          onChange={value => {
            const newValue = value.map(u => u.value)
            return onMetaChange('listingErrors', newValue)
          }}
          multi
          simpleValue={false}
          virtualized
        />
      )
    }
  ]
}

export const productCreatedAt = (meta: any, onMetaChange: Function): ColumnParams => ({
  id: 'product_created_at',
  Header: 'Product Created At',
  accessor: (d: any): JSX.Element => (
    <EmptyPlaceholder>{d.productCreatedAt && <InteractiveDatetime value={d.productCreatedAt} />}</EmptyPlaceholder>
  ),
  sortable: true,
  filterable: true,
  Filter: () => <DateRangeFilter meta={meta} onMetaChange={onMetaChange} metaKey="productCreatedAt" />,
  width: 145
})

export const isbn = (meta, onMetaChange): ColumnParams => ({
  Header: 'ISBN',
  id: 'ISBN',
  accessor: d => <EmptyPlaceholder>{d.isbn}</EmptyPlaceholder>,
  sortable: false,
  filterable: true,
  Filter: () => <SearchableFilter meta={meta} onMetaChange={onMetaChange} metaKey="isbn" />
})
